import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Icon } from "../Icon.jsx";

const PAGE_SIZES = [10, 25, 50, 100];

const formatter = new Intl.NumberFormat("id-ID");

function joinClasses(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function DataMasterSection({
  actions = null,
  children,
  className = "",
  error = "",
  eyebrow = "Modul aktif",
  message = "",
  meta = null,
  source = null,
  subtitle,
  title,
}) {
  return (
    <section className={joinClasses("master-section", "dm-section", className)}>
      <header className="dm-section-header">
        <div className="dm-section-header__text">
          {eyebrow && <span className="dm-section-header__eyebrow">{eyebrow}</span>}
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="dm-section-header__actions">{actions}</div>}
      </header>

      {(source || meta) && (
        <div className="dm-meta-strip">
          {source}
          {meta}
        </div>
      )}

      {message && <div className="notice-banner">{message}</div>}
      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}

      {children}
    </section>
  );
}

export function DataMasterToolbar({
  children,
  className = "",
  description = "Saring data sesuai kebutuhan operasional.",
  label = "Filter data",
  onSubmit,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const titleId = useId();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = [...panelRef.current.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    window.requestAnimationFrame(() => {
      panelRef.current?.querySelector(focusableSelector)?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [open]);

  function handleSubmit(event) {
    onSubmit?.(event);
    setOpen(false);
  }

  return (
    <div className="dm-filter-shell">
      <button
        aria-controls={panelId}
        aria-expanded={open}
        className="secondary-button dm-mobile-filter-trigger"
        onClick={() => setOpen(true)}
        type="button"
      >
        <Icon name="filter" size={16} />
        {label}
      </button>

      {open && (
        <button
          aria-label="Tutup filter"
          className="dm-filter-backdrop"
          onClick={() => setOpen(false)}
          type="button"
        />
      )}

      <section
        aria-labelledby={titleId}
        aria-modal={open ? "true" : undefined}
        className={joinClasses("dm-filter-panel", open && "is-open")}
        id={panelId}
        ref={panelRef}
        role={open ? "dialog" : undefined}
      >
        <header className="dm-filter-panel__header">
          <div>
            <strong id={titleId}>{label}</strong>
            <span>{description}</span>
          </div>
          <button
            aria-label="Tutup filter"
            className="dialog-close"
            onClick={() => setOpen(false)}
            type="button"
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        <form
          aria-label={label}
          className={joinClasses(
            "master-search",
            "master-filter-bar",
            "dm-toolbar",
            className,
          )}
          onSubmit={handleSubmit}
          {...props}
        >
          {children}
        </form>
      </section>
    </div>
  );
}

function annotateMobileTable(table) {
  const headers = [...table.querySelectorAll("thead th")].map((header) =>
    String(header.textContent || "").replace(/\s+/g, " ").trim(),
  );

  if (!headers.length) return;

  table.dataset.mobileReady = "true";

  if (!table.getAttribute("aria-label")) {
    const sectionTitle = table
      .closest(".dm-section")
      ?.querySelector(".dm-section-header h2")
      ?.textContent?.trim();
    table.setAttribute("aria-label", sectionTitle || "Data master");
  }

  table.querySelectorAll("tbody tr").forEach((row) => {
    [...row.children].forEach((cell, index) => {
      if (cell.tagName !== "TD" || cell.hasAttribute("colspan")) return;

      const label = headers[index] || `Kolom ${index + 1}`;
      cell.dataset.label = label;
      cell.classList.toggle("dm-mobile-card__primary", index === 0);
      cell.classList.toggle("dm-mobile-card__secondary", index === 1);
      cell.classList.toggle(
        "dm-mobile-card__actions",
        /aksi/i.test(label) || cell.classList.contains("master-action-cell"),
      );
    });
  });
}

export function DataMasterTableShell({
  children,
  className = "",
  emptyText = "Tidak ada data yang sesuai.",
  loading = false,
  mode = "viewport",
  rowCount = 0,
}) {
  const shellRef = useRef(null);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    let frame = 0;

    function annotate() {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        shell.querySelectorAll("table.master-table").forEach(annotateMobileTable);
      });
    }

    annotate();
    const observer = new MutationObserver(annotate);
    observer.observe(shell, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [children, loading, rowCount]);

  return (
    <div
      aria-busy={loading}
      className={joinClasses(
        "master-table-wrap",
        "dm-table-shell",
        mode === "compact" ? "dm-table-shell--compact" : "dm-table-shell--viewport",
        className,
      )}
      ref={shellRef}
    >
      {children}
      {loading && (
        <div className="master-empty dm-table-state" role="status">
          Memuat data...
        </div>
      )}
      {!loading && rowCount === 0 && (
        <div className="master-empty dm-table-state" role="status">
          {emptyText}
        </div>
      )}
    </div>
  );
}

export function DataMasterPagination({
  loading = false,
  onPageChange,
  onPageSizeChange,
  pagination,
  showPageSize = true,
}) {
  const page = Number(pagination?.page || 1);
  const pageSize = Number(pagination?.pageSize || 50);
  const pageCount = Number(pagination?.pageCount || 1);
  const rowCount = Number(pagination?.rowCount || 0);
  const start = rowCount ? (page - 1) * pageSize + 1 : 0;
  const end = rowCount ? Math.min(page * pageSize, rowCount) : 0;

  return (
    <footer className="master-pagination dm-pagination">
      <span>
        {rowCount
          ? `Menampilkan ${formatter.format(start)}–${formatter.format(end)} dari ${formatter.format(rowCount)} data`
          : "Menampilkan 0 data"}
      </span>

      <div className="dm-pagination__controls">
        {showPageSize && onPageSizeChange && (
          <label className="dm-page-size">
            <span>Baris</span>
            <select
              disabled={loading}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              value={pageSize}
            >
              {PAGE_SIZES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        )}

        <button
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          type="button"
        >
          Sebelumnya
        </button>
        <button
          disabled={loading || page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          type="button"
        >
          Berikutnya
        </button>
      </div>
    </footer>
  );
}

export function DataMasterRecordFooter({ count }) {
  return (
    <footer className="master-pagination dm-pagination">
      <span>Menampilkan {formatter.format(Number(count || 0))} data</span>
    </footer>
  );
}

export function DataMasterSourceBadge({
  readOnly = false,
  source = "Route Planning",
}) {
  return (
    <span className="dm-source-badge">
      Sumber: {source}
      {readOnly ? " · Hanya-baca" : ""}
    </span>
  );
}

export function DataMasterMetaText({ children }) {
  return <span className="dm-meta-text">{children}</span>;
}

export function DataMasterKpiStrip({
  children,
  hint = "Seluruh hasil filter aktif",
  label = "Ringkasan data",
}) {
  return (
    <section aria-label={label} className="dm-kpi-panel">
      <header className="dm-kpi-panel__header">
        <span>{label}</span>
        <small>{hint}</small>
      </header>
      <div className="dm-kpi-strip">{children}</div>
    </section>
  );
}

export function DataMasterKpiCard({
  helper = "",
  icon = "database",
  label,
  tone = "neutral",
  value,
}) {
  return (
    <article className={`dm-kpi-card dm-kpi-card--${tone}`}>
      <span className="dm-kpi-card__icon">
        <Icon name={icon} size={17} />
      </span>
      <div>
        <span>{label}</span>
        <strong>{formatter.format(Number(value || 0))}</strong>
        {helper && <small>{helper}</small>}
      </div>
    </article>
  );
}

export function DataMasterActiveFilters({ filters = [], onClear }) {
  const activeFilters = filters.filter(
    (filter) => filter && String(filter.value ?? "").trim() !== "",
  );

  return (
    <div className="dm-active-filters">
      <span className="dm-active-filters__label">Filter aktif</span>
      <div className="dm-active-filters__chips">
        {activeFilters.length ? (
          activeFilters.map((filter) => (
            <span key={`${filter.label}-${filter.value}`}>
              {filter.label}: {filter.value}
            </span>
          ))
        ) : (
          <span className="is-neutral">Semua data</span>
        )}
      </div>
      {activeFilters.length > 0 && onClear && (
        <button className="text-button" onClick={onClear} type="button">
          Bersihkan semua
        </button>
      )}
    </div>
  );
}

export function SyncStatusStrip({
  actions = null,
  lastSynced = "",
  lastSyncedLabel = "Terakhir Sinkron",
  metrics = [],
  progress = "",
  status = "",
  title = "",
}) {
  return (
    <div className="dm-sync-strip">
      <div className="dm-sync-strip__body">
        <strong>{title}</strong>
        <span>{status}</span>
        {lastSynced && <span>{lastSyncedLabel}: {lastSynced}</span>}
        {progress && <span>{progress}</span>}
      </div>

      {metrics.length > 0 && (
        <div className="dm-sync-strip__metrics">
          {metrics.map((metric) => (
            <span key={metric.label}>
              {metric.value} {metric.label}
            </span>
          ))}
        </div>
      )}

      {actions && <div className="dm-sync-strip__actions">{actions}</div>}
    </div>
  );
}

export function DataMasterRowActions({
  active = true,
  busy = false,
  deleteLabel = "Hapus",
  disabled = false,
  onDelete,
  onEdit,
  onToggle,
  toggleLabel,
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);
  const hasPrimaryAction = typeof onEdit === "function";
  const hasSecondaryActions =
    typeof onToggle === "function" || typeof onDelete === "function";

  useEffect(() => {
    if (!open) return undefined;

    function updatePosition() {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        left: Math.max(12, rect.right - 188),
        top: rect.bottom + 6,
      });
    }

    function handlePointerDown(event) {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    updatePosition();
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const menu = open && position
    ? createPortal(
        <div
          className="dm-row-action-menu"
          ref={menuRef}
          role="menu"
          style={{
            left: `${position.left}px`,
            top: `${position.top}px`,
          }}
        >
          {onToggle && (
            <button
              disabled={busy}
              onClick={() => {
                setOpen(false);
                onToggle();
              }}
              role="menuitem"
              type="button"
            >
              {toggleLabel || (active ? "Nonaktifkan" : "Aktifkan")}
            </button>
          )}

          {onDelete && (
            <button
              className="dm-row-action-menu__danger"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              role="menuitem"
              type="button"
            >
              {deleteLabel}
            </button>
          )}
        </div>,
        document.body,
      )
    : null;

  if (!hasPrimaryAction && !hasSecondaryActions) {
    return null;
  }

  return (
    <div className="master-actions dm-row-actions">
      {hasPrimaryAction && (
        <button
          className="master-action-button"
          disabled={busy || disabled}
          onClick={onEdit}
          type="button"
        >
          Edit
        </button>
      )}
      {hasSecondaryActions && (
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Aksi lainnya"
        className="master-action-button dm-overflow-button"
        disabled={busy || disabled}
        onClick={() => setOpen((current) => !current)}
        ref={buttonRef}
        title="Aksi lainnya"
        type="button"
      >
        ⋮
      </button>
      )}
      {menu}
    </div>
  );
}

export function DataMasterDrawer({
  busy = false,
  children,
  className = "",
  onClose,
  title,
}) {
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    function handleKeyDown(event) {
      if (event.key === "Escape" && !busy) onClose();
      if (event.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    const timer = window.setTimeout(() => {
      const target = panelRef.current?.querySelector(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
      );
      target?.focus();
    }, 80);

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      window.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [busy, onClose]);

  return createPortal(
    <div className="dm-drawer-backdrop" role="presentation">
      <aside
        aria-labelledby={titleId}
        aria-modal="true"
        className={joinClasses("dm-drawer", className)}
        ref={panelRef}
        role="dialog"
      >
        <header className="dm-drawer__header">
          <h2 id={titleId}>{title}</h2>
          <button
            aria-label="Tutup"
            className="dialog-close"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>
        {children}
      </aside>
    </div>,
    document.body,
  );
}

export function CompactOverflowMenu({ actions = [], disabled = false, label = "Aksi lainnya" }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);

  const enabledActions = useMemo(
    () => actions.filter(Boolean),
    [actions],
  );

  useEffect(() => {
    if (!open) return undefined;

    function updatePosition() {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        left: Math.max(12, rect.right - 220),
        top: rect.bottom + 6,
      });
    }

    function handlePointerDown(event) {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    updatePosition();
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        className="secondary-button dm-icon-button"
        disabled={disabled || enabledActions.length === 0}
        onClick={() => setOpen((current) => !current)}
        ref={buttonRef}
        title={label}
        type="button"
      >
        ⋮
      </button>

      {open && position
        ? createPortal(
            <div
              className="dm-row-action-menu"
              ref={menuRef}
              role="menu"
              style={{
                left: `${position.left}px`,
                top: `${position.top}px`,
              }}
            >
              {enabledActions.map((action) => (
                <button
                  className={action.danger ? "dm-row-action-menu__danger" : ""}
                  disabled={action.disabled}
                  key={action.label}
                  onClick={() => {
                    setOpen(false);
                    action.onClick?.();
                  }}
                  role="menuitem"
                  type="button"
                >
                  {action.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
